import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  Suspense,
} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { styles } from "./modules/shared/styles";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import LoginPage from "./LoginPage";
import { CircularProgress } from "@mui/material";
import ErrorBoundary from "./ErrorBoundary";
import { SearchResultsProvider } from "./hooks/SearchResultsContext";
import {
  hasExplicitPermissions,
  resolvePermission,
} from "./modules/role-constructor/utils/accessUtils";
import {
  menuItems,
  profileMenuItems,
  adminProfileItems,
  buildReferenceInfoTabs,
  buildWarehouseTabs,
  buildDeliveryTabs,
  buildAnalogSettingsTabs,
  buildNavIndex,
  CommandPalette,
} from "./components/navigation";
import { AuthContext, AuthProvider } from "./app/providers/auth";
import AppRoutes from "./app/routes";
import AppSidebar from "./app/AppSidebar";
// Реэкспорт ради обратной совместимости со старыми импортами из "./App"
export { AuthContext, AuthProvider };
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;


function App() {
  const navigate = useNavigate();
  const { logout, role, user, loading } = useContext(AuthContext);
  const location = useLocation();
  const [priceLists, setPriceLists] = useState([]);
  const [error, setError] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [modalOpenCount, setModalOpenCount] = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Глобальный хоткей Ctrl/⌘ + K — открыть поиск по вкладкам
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const userMenuRef = useRef(null);
  // bitrixElements теперь загружается через useQuery

  const handleLogout = async () => {
    try {
      await logout();
      // Очищаем все кэши React Query при выходе
      queryClient.clear();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Ошибка при выходе:", error);
      navigate("/login", { replace: true });
    }
  };

  // Загружаем bitrixElements через useQuery (убрали бесконечный цикл)
  const { data: bitrixElements = [] } = useQuery({
    queryKey: ["bitrixElements"],
    queryFn: async () => {
      const response = await fetch(
        `${PATH_TO_BACKEND}/api/adminka/bitrix_elements_full?limit=50000`,
        {
          credentials: "include",
        },
      );
      const result = await response.json();
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 минут
  });

  // Heartbeat - поддержание соединения активным
  useEffect(() => {
    const heartbeatInterval = setInterval(
      async () => {
        try {
          await fetch(`${PATH_TO_BACKEND}/api/check-session`, {
            credentials: "include",
          });
          console.log("Heartbeat: соединение активно");
        } catch (err) {
          console.error("Heartbeat error:", err);
        }
      },
      5 * 60 * 1000,
    ); // каждые 5 минут

    return () => clearInterval(heartbeatInterval);
  }, []);

  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === "/") return "main";
    if (path.startsWith("/reference-info")) return "reference-info";
    if (path.startsWith("/warehouses")) return "warehouses";
    if (path.startsWith("/delivery")) return "delivery";
    if (path.startsWith("/analog-settings")) return "analog-settings";
    if (path === "/missing-data" || path === "/missing_data")
      return "missing-data";
    if (path === "/login") return "login";
    if (path === "/warnings") return "warnings";
    if (path === "/all-sources") return "all-sources";
    // Добавляем обработку всех возможных маршрутов
    if (path.startsWith("/")) {
      const segments = path.split("/").filter(Boolean);
      if (segments.length > 0) {
        // Возвращаем первый сегмент как секцию
        return segments[0];
      }
    }
    return "unknown";
  };

  const currentSection = getCurrentSection();
  const userPermissions = user?.permissions || {};
  const hasRolePermissions = hasExplicitPermissions(userPermissions);
  const isManager =
    !hasRolePermissions && (role === "manager" || role === "менеджер");
  const isLogist =
    !hasRolePermissions && ["СПБ", "МСК", "КРД", "ЛО"].includes(role);
  const userDisplayName =
    user?.fullName || user?.username || user?.bitrixLogin || "Пользователь";
  const avatarUrl =
    user?.avatar ||
    user?.photo ||
    user?.photoUrl ||
    user?.avatarUrl ||
    user?.personalPhoto ||
    user?.personalPhotoUrl;
  const userInitials =
    userDisplayName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "U";

  const handlePriceListsUpdate = (newPriceLists) => {
    setPriceLists(newPriceLists);
  };

  const referenceInfoTabs = buildReferenceInfoTabs({
    role,
    permissions: userPermissions,
    isManager,
  });
  const warehouseTabs = buildWarehouseTabs({
    role,
    permissions: userPermissions,
    isManager,
    priceLists,
  });
  const deliveryTabs = buildDeliveryTabs({
    role,
    permissions: userPermissions,
    isManager,
  });
  const analogSettingsTabs = buildAnalogSettingsTabs({
    role,
    permissions: userPermissions,
  });
  const queryClient = useQueryClient();

  // Отслеживаем смену роли и очищаем кэш
  useEffect(() => {
    if (role) {
      // Очищаем кэш при смене роли для обновления фильтров
      queryClient.invalidateQueries();
    }
  }, [role, queryClient]);

  // Автоматическое перенаправление с неизвестных маршрутов
  useEffect(() => {
    const currentSection = getCurrentSection();
    if (currentSection === "unknown" && location.pathname !== "/login") {
      const timer = setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const fetchPriceLists = async () => {
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ["listsOfPrices"],
          queryFn: async () => {
            const response = await fetch(
              `${PATH_TO_BACKEND}/api/adminka/price_lists`,
              {
                credentials: "include",
              },
            );
            const responseJson = await response.json();
            return responseJson || [];
          },
        });

        if (result.success) {
          setPriceLists(result.data || []);
          setError(null);
        } else {
          console.error("Ошибка загрузки прайс-листов:", result.error);
          setError(result.error || "Ошибка загрузки прайс-листов");
        }
      } catch (err) {
        console.error("Ошибка при загрузке прайс-листов:", err);
        setError(err.message || "Ошибка при загрузке прайс-листов");
      }
    };

    fetchPriceLists();
  }, [queryClient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
        setIsAdminMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Глобальное управление стилями <body> при открытии/закрытии модальных окон
  useEffect(() => {
    if (modalOpenCount > 0) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      document.body.style.overflowX = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.overflowX = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.overflowX = "";
    };
  }, [modalOpenCount]);

  const openModal = () => {
    setModalOpenCount((prev) => prev + 1);
  };

  const closeModal = () => {
    setModalOpenCount((prev) => Math.max(prev - 1, 0));
  };

  const handleSubMenuClick = (section) => {
    setActiveSubMenu(activeSubMenu === section ? null : section);
    let firstTabPath;
    if (section === "reference-info" && referenceInfoTabs.length > 0) {
      firstTabPath = referenceInfoTabs[0].path;
    } else if (section === "warehouses" && warehouseTabs.length > 0) {
      // - Первым пунктом раздела идёт загрузчик прайсов, а он лежит вне /warehouses.
      //   Клик по заголовку раздела должен по-прежнему открывать складскую вкладку,
      //   а не загрузчик, поэтому берём первую вкладку с путём /warehouses/.
      const firstWarehouseTab = warehouseTabs.find((tab) =>
        tab.path.startsWith("/warehouses/"),
      );
      firstTabPath = (firstWarehouseTab || warehouseTabs[0]).path;
    } else if (section === "delivery" && deliveryTabs.length > 0) {
      firstTabPath = deliveryTabs[0].path;
    } else if (section === "analog-settings" && analogSettingsTabs.length > 0) {
      firstTabPath = analogSettingsTabs[0].path;
    }
    if (firstTabPath && location.pathname !== firstTabPath) {
      navigate(firstTabPath);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const canAccessItem = (item) => {
    if (item.permissionKey) {
      const permission = resolvePermission({
        role,
        permissions: userPermissions,
        permissionKey: item.permissionKey,
      });

      if (hasExplicitPermissions(userPermissions)) {
        return permission.canView;
      }
    }

    if (!item.access) return true;
    if (Array.isArray(item.access)) {
      return item.access.includes(role);
    }
    return item.access === role;
  };

  const hasVisiblePermissionByPrefix = (prefix) => {
    if (!hasExplicitPermissions(userPermissions)) {
      return false;
    }

    return Object.entries(userPermissions).some(
      ([permissionKey, permission]) =>
        permissionKey.startsWith(`${prefix}.`) && permission?.canView,
    );
  };

  const canShowSectionItem = (item) => {
    if (!item?.section || !item.hasDropdown) {
      return canAccessItem(item);
    }

    if (!hasExplicitPermissions(userPermissions)) {
      return canAccessItem(item);
    }

    const directAccess = canAccessItem(item);

    if (item.section === "reference-info") {
      return directAccess || hasVisiblePermissionByPrefix("reference-info");
    }

    if (item.section === "warehouses") {
      return directAccess || hasVisiblePermissionByPrefix("warehouses");
    }

    if (item.section === "delivery") {
      return directAccess || hasVisiblePermissionByPrefix("delivery");
    }

    if (item.section === "analog-settings") {
      return directAccess || hasVisiblePermissionByPrefix("analog-settings");
    }

    return directAccess;
  };

  const visibleProfileMenuItems = profileMenuItems.filter(canAccessItem);
  const visibleAdminProfileItems = adminProfileItems.filter(canAccessItem);

  const handleProfileNavigation = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
    setIsAdminMenuOpen(false);
  };

  if (loading) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="7rem" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Фильтруем menuItems в зависимости от роли пользователя
  const filteredMenuItems = menuItems.filter((item) =>
    canShowSectionItem(item),
  );

  // Плоский индекс всех доступных вкладок для командной палитры (Ctrl/⌘ + K)
  const navIndex = buildNavIndex({
    referenceInfoTabs,
    warehouseTabs,
    deliveryTabs,
    analogSettingsTabs,
    profileItems: visibleProfileMenuItems,
    adminItems: visibleAdminProfileItems,
  });

  return (
    <SearchResultsProvider>
        <div style={{ ...styles.pageContainer, background: "var(--ui-bg)" }}>
          <CommandPalette
            items={navIndex}
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            onNavigate={(path) => navigate(path)}
          />
          <AppSidebar
            isSidebarVisible={isSidebarVisible}
            toggleSidebar={toggleSidebar}
            activeSubMenu={activeSubMenu}
            handleSubMenuClick={handleSubMenuClick}
            currentSection={currentSection}
            role={role}
            isManager={isManager}
            filteredMenuItems={filteredMenuItems}
            referenceInfoTabs={referenceInfoTabs}
            warehouseTabs={warehouseTabs}
            deliveryTabs={deliveryTabs}
            analogSettingsTabs={analogSettingsTabs}
            priceLists={priceLists}
            location={location}
            isUserMenuOpen={isUserMenuOpen}
            setIsUserMenuOpen={setIsUserMenuOpen}
            isAdminMenuOpen={isAdminMenuOpen}
            setIsAdminMenuOpen={setIsAdminMenuOpen}
            userMenuRef={userMenuRef}
            visibleProfileMenuItems={visibleProfileMenuItems}
            visibleAdminProfileItems={visibleAdminProfileItems}
            handleProfileNavigation={handleProfileNavigation}
            handleLogout={handleLogout}
            setPaletteOpen={setPaletteOpen}
            avatarUrl={avatarUrl}
            userDisplayName={userDisplayName}
            userInitials={userInitials}
          />
          <div
            style={{
              ...styles.mainContent,
              marginLeft: isSidebarVisible ? "10px" : "0",
              marginRight: "10px",
              borderRadius: "12px",
              // overflow: 'hidden',
              background: "var(--ui-surface)",
              boxShadow: "var(--ui-shadow-lg)",
              marginTop: "5px",
              transition: "margin-left 0.1s ease",
              width: isSidebarVisible
                ? "calc(100% - 300px)"
                : "calc(100% - 10px)",
              boxSizing: "border-box",
            }}
          >
            {!isSidebarVisible && (
              <button
                onClick={toggleSidebar}
                style={{
                  position: "fixed",
                  top: "20px",
                  left: "15px",
                  background: "var(--ui-primary)",
                  color: "var(--ui-surface)",
                  border: "none",
                  borderRadius: "8px",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "var(--ui-shadow-md)",
                  zIndex: 1000,
                  transition: "background 0.2s ease",
                }}
              >
                ☰
              </button>
            )}
            {error ? (
              <div style={styles.error}>
                {error}
                {error.includes("Сессия истекла") && (
                  <button
                    onClick={() => navigate("/login")}
                    style={styles.addButton}
                  >
                    Войти снова
                  </button>
                )}
              </div>
            ) : (
              <ErrorBoundary>
                {/*
                  key по разделу — обязателен. react-router 7 обновляет location
                  внутри React.startTransition, а React во время transition НЕ
                  показывает fallback уже раскрытого <Suspense>: он держит на
                  экране предыдущий раздел, пока не догрузится lazy-чанк нового
                  (все разделы в app/routes.jsx — React.lazy). history.pushState
                  при этом срабатывает сразу, поэтому наружу это выглядело как
                  «url сменился, а страница осталась статической» — например при
                  переходе на «Проведённые реализации» (/warehouses/load1_structured),
                  чанк которой один из самых тяжёлых.
                  Смена key пересоздаёт границу Suspense, и fallback показывается
                  сразу же. Внутри одного раздела (переключение вкладок) key не
                  меняется — лишних перемонтирований не добавляется.
                */}
                <Suspense
                  key={currentSection}
                  fallback={
                    <div
                      style={{
                        width: "100%",
                        minHeight: "60vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size="4rem" />
                    </div>
                  }
                >
                <AppRoutes
                  handlePriceListsUpdate={handlePriceListsUpdate}
                  priceLists={priceLists}
                  bitrixElements={bitrixElements}
                  canAccessItem={canAccessItem}
                />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
        </div>
        
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
    </SearchResultsProvider>
  );
}

export default App;
